import * as localforage from "localforage";
import _ from "lodash";

import type {ImgSymbolType, SvgSymbolType, SymbolType} from "@esWebAPI/es/model/SymbolType";
import type {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import type {ILayerView} from "@esWebAPI/es/model/Layer";
import type {ColorThematic, SizeThematic, SymbolThematic, ThematicColorEntry} from "@esWebAPI/es/model/Thematic";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import {DefaultColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {TerritoryPointStyleSettings} from "@esWebAPI/es/model/Models";

export class ImageSpriteGenerator {

    private readonly BATCH_SIZE = 100;
    private readonly MAX_CANVAS_SIZE = 4000;

    private readonly imageSpriteManager: AzureImageSpriteManager;
    private readonly lfStore: any;
    private readonly isTightBoxLabel: boolean = false; // todo: temporary. remove after box labels are reviewed
    private readonly _territoryModel;

    constructor(imageSpriteManager: AzureImageSpriteManager, territoryModel?: TerritoryModel) {
        this.imageSpriteManager = imageSpriteManager;
        this.lfStore = localforage.createInstance({name: "es3", storeName: 'symbolsData'});
        this.isTightBoxLabel = es.util.localStorage.get("generateBoxImage") === "tight";
        this._territoryModel = territoryModel;
    }

    /**
     * Create a canvas to draw the symbols in
     */
    private getDrawingContext(id: string, width: number, height: number): {canvasId: string, context: CanvasRenderingContext2D} {
        const canvasId = `sprite-generator-canvas-${id}-${new Date().valueOf()}`
        let style = 'background-color: white; position: absolute; display: none;'


        const item = sessionStorage.getItem("esDebugSpriteCanvas");
        const esDebugSpriteCanvas = item ? JSON.parse(item) : false;

        if (esDebugSpriteCanvas) {
            style += ' left: 300px; top:10px; display: block';
        }

        const canvas: HTMLCanvasElement = document.createElement("canvas");
        canvas.setAttribute("id", canvasId);
        canvas.setAttribute("style", style);
        $("#mainMap").append(canvas);


        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d", {willReadFrequently: true});
        if (!ctx) {
            throw new Error("Unable to init canvas context: " + canvasId);
        }
        return {canvasId: canvasId, context: ctx};
    }

    public async getSymbolTypes(layerView: ILayerView, symbolThematic?: SymbolThematic) {

        const defaultStyle = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            layerView.styleSettings.defaultStyle.symbolStyle :
            layerView.styleSettings.defaultStyle;

        const symbolTypeIds: number[] = [];

        if (symbolThematic) {
            symbolThematic.ranges.forEach((r: any) => {
                if (!symbolTypeIds.includes(r.symbolTypeId)) {
                    symbolTypeIds.push(r.symbolTypeId);
                }
            });
        }
        if (!symbolTypeIds.includes(defaultStyle.symbolTypeId)) {
            symbolTypeIds.push(defaultStyle.symbolTypeId);
        }
        return es.api.symbolType.getByIds(symbolTypeIds) as JQueryPromise<SymbolType[]>;
    }

    public async generateImages(layerView: ILayerView,
                   symbolThematic?: SymbolThematic,
                   sizeThematic?: SizeThematic,
                   colorThematic?: ColorThematic): Promise<void> {

        this.imageSpriteManager.eventChannel.fireEvent("onGenerateImagesStart");

        let colors: number[] = [];

        const defaultStyle = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            layerView.styleSettings.defaultStyle.symbolStyle :
            layerView.styleSettings.defaultStyle;

        if (colorThematic) {
            colorThematic.ranges.forEach((r: ThematicColorEntry) => {
                if (!colors.includes(r.color)) {
                    colors.push(r.color);
                }
            });
        }
        if (layerView.styleSettings.colorPerFeatureSettings?.enabled) {
            const perFeatureColors = await DefaultColorThematicStyler.getColorPerFeatureColors(layerView.id);
            perFeatureColors.forEach(c=> {
                if (!colors.includes(c)) {
                    colors.push(c);
                }
            })
        }
        if (!colors.includes(defaultStyle.color)) {
            colors.push(defaultStyle.color);
        }

        if (this._territoryModel &&
            layerView.styleSettings.$type === "TerritoryPointStyleSettings" &&
            (layerView.styleSettings as TerritoryPointStyleSettings).useTerritoryColor) {

            const territoryColors = this._territoryModel.territoryFeatures.map(t => t.ES_STYLE_COLOR);
            colors.push(...territoryColors);
        }
        colors = _.uniq(colors);

        const simplify = !layerView.styleSettings.colorPerFeatureSettings?.enabled

        const symbolTypes = await this.getSymbolTypes(layerView, symbolThematic);

        const itemSize = sizeThematic && layerView.styleSettings.sizeThematicSettings?.enabled ? sizeThematic.maxSize : defaultStyle.size;
        const nbRows = symbolTypes.length;
        const nbCols = colors.length;

        const width = Math.min(this.MAX_CANVAS_SIZE, nbCols * itemSize);
        const height = Math.min(this.MAX_CANVAS_SIZE, nbRows * itemSize);

        const maxColsPerCanvas = Math.floor(this.MAX_CANVAS_SIZE / itemSize);

        const ctx = this.getDrawingContext(String(layerView.id), width, height);
        const ctxSelected = this.getDrawingContext(`${layerView.id}_selected`, width * 2, height * 2);

        return new Promise<void>((resolve, reject) => {

            const actionItems: GenerateImageActionItem[] = [];

            let cols = nbCols;
            let i = 0;
            while (cols > 0) {

                let startIndex = (maxColsPerCanvas * i);
                let endIndex = (maxColsPerCanvas * (i+1));
                let pageColors = colors.slice(startIndex, endIndex);

                symbolTypes.forEach((symbolType, row: number) => {
                    if (symbolType.$type === "ImgSymbolType") {
                        actionItems.push({context: ctx.context, symbolType, size: itemSize, color: 0, col: 0, row: 0, simplify});
                    }
                    else if (symbolType.$type === "SvgSymbolType") {
                        pageColors.forEach((color, col: number) => {
                            actionItems.push({context: ctx.context,symbolType, size: itemSize, color, col, row, simplify});
                            actionItems.push({context: ctxSelected.context, symbolType, size: itemSize, color, col, row, simplify, isSelectionStyle: true});
                        });
                    }
                    else {
                        reject("Unsupported symbol type: " + JSON.stringify(symbolType));
                    }
                });
                cols -= pageColors.length;
                i++;
            }

            const queue: GenerateImageActionItem[][] = [];
            const total = actionItems.length; //Keep track of the original number of symbols
            while (actionItems.length > 0 ) {
                const batch = actionItems.splice(0, this.BATCH_SIZE);
                queue.push(batch);
            }
           this.startQueue(queue, total, [ctx.canvasId, ctxSelected.canvasId], resolve);
        });
    }

    /**
     * Resize anc colorize an SVG element
     * @param svg
     * @param size
     * @param color
     * @private
     */
    private prepareSVGTemplate(svg: string, size: number, color?: string) {
        const svgDoc = new DOMParser().parseFromString(svg, "image/svg+xml");
        const svgEl = svgDoc.documentElement;
        const width = +((svgEl.getAttribute("width")||"0").replace("px",""));
        const height = +((svgEl.getAttribute("height")||"0").replace("px",""));

        const scale = size / Math.max(width, height);
        svgEl.setAttribute("width", width*scale+"px");
        svgEl.setAttribute("height", height*scale+"px");

        if(svgDoc.getElementById("filled")) {
            this.colorize(svgDoc.getElementById("filled"), color);
        }

        let i = 1;
        let id = "filled" + i;
        while (this.colorize(svgDoc.getElementById(id), color)) {
            i++;
            id = "filled" + i;
        }

        return svgDoc.documentElement.outerHTML;
    }

    private colorize(element: HTMLElement|null, color?: string) {
        if (!element) {
            return false;
        }
        element.setAttribute("fill", color || "{color}");
        return true;
    }

    private startQueue(queue: GenerateImageActionItem[][], total: number, canvasId: string[],  resolve: () => void) {

        const end = () => {
            resolve();
            canvasId.forEach(canvasId => $("#"+canvasId).remove());
            this.imageSpriteManager.eventChannel.fireEvent("onGenerateImagesFinish");
        }

        if (queue.length > 0) {
            const batch = queue.shift();
            if(!batch || batch.length === 0) {
                end();
                return;
            }

            const promises = batch.map(item =>
                this.processItem(item)
            );
            Promise.all(promises).then(() => {
                if(total > 100) {
                    const progress = Math.ceil(((total - (queue.length * this.BATCH_SIZE)) / total) * 100);
                    $('#loadingMessageText').text("Generating symbols " + progress + "%");
                }
                this.startQueue(queue, total, canvasId, resolve);
            });
        } else {
            end();
        }
    }

    private processItem(item: GenerateImageActionItem): Promise<string> {
        return new Promise<string>((resolve, reject) => {

            const templateName = `es_${item.symbolType.id}_${item.size}`;

            if (item.symbolType.$type === "SvgSymbolType") {

                let color: number = item.simplify ? es.util.color.simplify(es.util.color.serverToHex(item.color)) : item.color;
                let size = item.size;

                let imageId = `es_${item.symbolType.id}_${item.size}_${color}`;

                if (item.text) {
                    imageId = `${imageId}_${item.text}`
                }

                if (item.isSelectionStyle) {
                    imageId = `${imageId}_sel`
                }

                if (item.isSelectionStyle) {
                    color = es.util.color.getShadeServerColor(color, -0.5);
                    size *= 2;
                }

                this.lfStore.getItem(imageId).then((cache: Uint8ClampedArray) => {
                    if (!cache) {

                        // Resize and colorize the svg

                        const svg = this.prepareSVGTemplate((item.symbolType as SvgSymbolType).svg, size, es.util.color.serverToHex(color));
                        // Convert the svg to base64
                        const b64 = "data:image/svg+xml;base64," + window.btoa(svg);

                        // To get an ImageData/Uint8ClampedArray we need to load the image and draw it on a canvas
                        // Load the base64 data into an Image object;
                        const image = new Image();
                        image.onload = () => {

                            item.context.clearRect(item.col * size, item.row * size, size, size)

                            //Draw the image on a canvas
                            item.context.drawImage(image, (item.col * size) + ((size - image.width)/2), item.row * size);

                            if (item.text) {
                                //https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Drawing_text
                                const textX = (item.col * size) + ((size - image.width)/2) + size/2;
                                const textY = ((item.row+1) * size) * 0.5  + 0.5;
                                item.context.font = "bold 14px sans-serif";
                                item.context.fillStyle = "white";
                                item.context.textAlign = "center";
                                item.context.textBaseline = "middle";
                                item.context.fillText(item.text, textX, textY);
                            }

                            //Get the ImageData object
                            const imgData = item.context.getImageData(item.col * size, item.row * size, size, size);
                            //Store the Uint8ClampedArray data in LocalForage/Indexed DB
                            this.lfStore.setItem(imageId, imgData.data);
                            //Add the ImageData to Azure
                            this.imageSpriteManager.add(imageId, imgData).then(() => {
                                resolve(imageId);
                            }).catch((e) => {
                                es.log.error("ImageSpriteGenerator.imageSpriteManager.add  error", e);
                            });
                        };
                        image.onerror = (e) => {
                            es.log.error("ImageSpriteGenerator.image.load  error", e);
                        }
                        image.src = b64;
                    } else {
                        // Create a new ImageData object from Uint8ClampedArray stored in LocalForage/Indexed DB and load it into Azure/MapLibre
                        this.imageSpriteManager.add(imageId, new ImageData(cache, size, size)).then(() => {
                            resolve(imageId);
                        }).catch((e) => {
                            es.log.error("ImageSpriteGenerator.imageSpriteManager.add  error", e);
                        });
                    }
                });
            }
            else if (item.symbolType.$type === "ImgSymbolType") {
                let imageId = templateName;
                let size = item.size;
                if (item.isSelectionStyle) {
                    imageId += "_sel";
                    size *= 2;
                }

                const image = new Image(size, size);
                image.onload = () => {
                    this.imageSpriteManager.add(imageId, image).then(() => resolve(imageId));
                };
                image.src = "data:image/png;base64," + (item.symbolType as ImgSymbolType).base64Img;
            }
            else {
                reject("Unsupported symbol type: " + JSON.stringify(item.symbolType));
            }
        });
    }

    public generateBoxImage(): string {
        return this.isTightBoxLabel ? this._generateBoxImageTight() : this._generateBoxImageStandard();
    }

    public async generateRouteBadge(style: any) {

        const context = this.getDrawingContext("schedule-symbol", style.size, style.size);
        const symbolType = await es.api.symbolType.get(style.symbolTypeId) as SvgSymbolType;

        const item: GenerateImageActionItem = {
            context: context.context,
            symbolType,
            size: style.size,
            color: style.color,
            simplify: false,
            col: 0,
            row: 0,
            isSelectionStyle: false,
            text: style.text
        };

        const imageId = await this.processItem(item);
        $("#"+context.canvasId).remove();
        return imageId;
    }

    /**
     * Generate a stretchable SDF image to use as label box background
     * @return The image id of the generated image
     */
    private _generateBoxImageStandard(): string {
        // todo: these vals seem odd but visually (to me anyway) seem to work. should review

        const imageSize = 40;
        const ctx = this.getDrawingContext(`box`, imageSize, imageSize).context;

        ctx.filter = 'blur(2px)'; //Allow the icon-halo to work properly
        ctx.beginPath();
        ctx.roundRect(4.5, 11, 31, 18, [8]);
        ctx.fillStyle = "#000000";
        ctx.fill();

        const imageData = ctx.getImageData(0,0,imageSize, imageSize);
        const imageId = `box`;
        this.imageSpriteManager.add(imageId, imageData, {
            stretchX: [[15, 25]],
            stretchY: [[16, 24]],
            content: [15, 16, 25, 24],
            pixelRatio: 32,
            sdf: true
        });
        return imageId;
    }

    private _generateBoxImageTight(): string {
        const imageSize = 40;
        const ctx = this.getDrawingContext(`box`, imageSize, imageSize).context;

        ctx.filter = 'blur(2px)'; //Allow the icon-halo to work properly
        ctx.beginPath();
        ctx.roundRect(4, 13, 32, 12, [4]);
        ctx.fillStyle = "#000000";
        ctx.fill();

        const imageData = ctx.getImageData(0,0,imageSize, imageSize);
        const imageId = `box`;

        this.imageSpriteManager.add(imageId, imageData, {
            stretchX: [[10, 30]],
            stretchY: [[16, 22]],
            content: [8, 12, 32, 24],
            pixelRatio: 32,
            sdf: true
        });
        return imageId;
    }
}

interface GenerateImageActionItem {
    context: CanvasRenderingContext2D,
    symbolType: SymbolType,
    size: number,
    color: number,
    simplify: boolean,
    col: number,
    row: number,
    isSelectionStyle?: boolean,
    text?: string
}
